export type RequestDetailsTab = 'info' | 'invoices' | 'comments'

/** Request card state kept in the URL: `/requests/310?tab=invoices&invoice=341`. */
export interface RequestDetailsView {
  tab: RequestDetailsTab
  invoiceId: number | null
}

export const DEFAULT_REQUEST_DETAILS_VIEW: RequestDetailsView = { tab: 'info', invoiceId: null }

const TAB_PARAM = 'tab'
const INVOICE_PARAM = 'invoice'
const URL_TABS: readonly RequestDetailsTab[] = ['invoices', 'comments']

function parseInvoiceId(rawValue: string | null): number | null {
  if (!rawValue || !/^\d+$/.test(rawValue)) {
    return null
  }

  const parsed = Number(rawValue)
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null
}

export function parseRequestDetailsView(searchParams: URLSearchParams): RequestDetailsView {
  const rawTab = searchParams.get(TAB_PARAM)
  const tab = URL_TABS.find((value) => value === rawTab) ?? 'info'

  return {
    tab,
    invoiceId: tab === 'invoices' ? parseInvoiceId(searchParams.get(INVOICE_PARAM)) : null
  }
}

/** Returns new params with the view written over the given ones; the default info tab is never written. */
export function applyRequestDetailsView(searchParams: URLSearchParams, view: RequestDetailsView): URLSearchParams {
  const next = new URLSearchParams(searchParams)
  next.delete(TAB_PARAM)
  next.delete(INVOICE_PARAM)

  if (view.tab === 'info') {
    return next
  }

  next.set(TAB_PARAM, view.tab)
  if (view.tab === 'invoices' && view.invoiceId !== null) {
    next.set(INVOICE_PARAM, String(view.invoiceId))
  }

  return next
}

/** Drops the request card view from a search string, keeping the list params (filters, page). */
export function stripRequestDetailsView(search: string): string {
  const query = applyRequestDetailsView(new URLSearchParams(search), DEFAULT_REQUEST_DETAILS_VIEW).toString()
  return query ? `?${query}` : ''
}

/** Builds a request card path carrying the view, e.g. `/requests/310?tab=invoices&invoice=341`. */
export function buildRequestDetailsPath(requestId: number, view: RequestDetailsView): string {
  const query = applyRequestDetailsView(new URLSearchParams(), view).toString()
  return query ? `/requests/${requestId}?${query}` : `/requests/${requestId}`
}
